// src/components/DetailAgenda.js
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import DetailPersonnel from "./DetailPersonnel";
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";
import PERMISSIONS, { hasUmatPermission } from "../utils/wkCommon";
import { getUser } from "../utils/auth"
import CardAgendaActions from "./CardAgendaActions";
import { fetchLunarForIntDate } from "../utils/wkCommon";
import { isZh } from "../utils/language";

/**
 * DetailAgenda
 * Uses only fields present in the Agenda content-type:
 * - subject
 * - agendaDate
 * - agendaDateEnd
 * - hoursInfo
 * - agenda_master (relation)
 * - area (relation)
 * - FaHui, Highlight, allowPreRegistration, onlineClass, notes, category_age_groups
 *
 * All font sizes / spacing are in rem units.
 */

const css = `
.detail-agenda{font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;color:#10203a;display:flex;flex-direction:column;gap:0.25rem;padding-right:1.4rem;background:transparent}
.da-toprow{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem}
.da-dateblock{display:flex;flex-direction:column;gap:0.125rem}
.da-week{font-size:0.9rem;font-weight:800;color:#0f1724;line-height:1}
.da-date-main{font-size:1rem;font-weight:900;color:#0f1724}
.da-date-range{font-size:0.75rem;color:#475569}
.da-titlewrap{margin-top:0.25rem;display:flex;flex-direction:column;gap:0.125rem;align-items:flex-start;text-align:left}
.da-title{padding-left:0.4rem;font-size:1.125rem;font-weight:800;color:#0b63a8;line-height:1.1;text-decoration:none;text-align:left}
.da-subtitle{padding-left:0.4rem;font-style:italic;font-weight:600;font-size:0.9rem;color:#0b63a8;margin-top:0.125rem}
.da-chinese{font-size:0.875rem;color:#0b63a8;margin-top:0.125rem}
.da-titlewrap .area-badge{display:inline-block;padding:0.4rem 0.55rem;border-radius:0.375rem;font-size:0.9rem;font-weight:600;line-height:1;margin-bottom:0.15rem;align-self:flex-start;box-shadow:0 0 0 1px rgba(16,32,58,0.02) inset}
.da-titlewrap .area-badge.area-cat1{background:#e8f0ff;color:#133b8a}
.da-titlewrap .area-badge.area-cat2{background:#efe8ff;color:#4b2f6b}
.da-titlewrap .area-badge.area-cat3{background:#e6f7e9;color:#146b2e}
.da-row{display:flex;flex-direction:column;gap:0.375rem;padding:0.125rem 0 0.5rem 0;border-bottom:1px dashed rgba(16,32,58,0.06)}
.da-label{font-size:0.6875rem;color:#6b7280;font-weight:700;letter-spacing:0.04rem;text-transform:uppercase}
.da-value{font-size:0.95rem;color:#0f1724}
.da-meta{display:flex;gap:0.5rem;flex-wrap:wrap;margin-top:0.25rem}
.meta-pill{background:#f1f5f9;padding:0.1875rem 0.5rem;border-radius:0.5rem;font-size:0.8125rem;color:#1f2937;display:inline-flex;align-items:center;gap:0.375rem}
.da-notes{white-space:pre-wrap;font-size:0.9rem;color:#263238;line-height:1.4;margin-top:0.25rem}
.actions{display:flex;gap:0.5rem;justify-content:flex-end;margin-top:0.5rem}
.btn{font-weight:700;padding:0.45rem 0.75rem;border-radius:0.5rem;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:0.375rem;font-size:0.875rem}
.btn-primary{background:#0b63a8;color:#fff;border:none}
.btn-ghost{background:transparent;color:#0b63a8;border:1px solid rgba(11,99,168,0.12)}
.empty{padding:1rem;font-size:0.95rem;color:#6b7280;text-align:center}
`;

/* --- helpers --- */
function decodeHtmlEntities(str) {
  if (!str || typeof str !== "string") return str;
  try { const txt = document.createElement("textarea"); txt.innerHTML = str; return txt.value; } catch (e) { return str; }
}
function decodeUnicodeEscapes(str) {
  if (!str || typeof str !== "string") return str;
  try { const s = str.replace(/\\\\u/g, "\\u"); return s.replace(/\\u([\dA-Fa-f]{4})/g, (_, g) => String.fromCharCode(parseInt(g, 16))); } catch (e) { return str; }
}
function decodeNumericEntities(str) {
  if (!str || typeof str !== "string") return str;
  return str.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
}
function decodeText(raw) {
  if (!raw) return raw;
  let s = String(raw).trim();
  s = decodeHtmlEntities(s);
  s = decodeNumericEntities(s);
  s = decodeUnicodeEscapes(s);
  s = s.replace(/\u0000/g, "").replace(/\uFFFD/g, "");
  return s;
}
function formatWeekday(tsOrIso) {
  if (!tsOrIso) return "";

  const zhWeek = ["週日","週一","週二","週三","週四","週五","週六"];
  const indoDays = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
  const dt = new Date(tsOrIso);
  return isZh() ? zhWeek[dt.getUTCDay()] : indoDays[dt.getUTCDay()]
}
function formatDateShort(tsOrIso) {
  if (!tsOrIso) return "";
  try {
    const d = new Date(tsOrIso);
    if (isNaN(d)) return String(tsOrIso).slice(0, 10);

    if (isZh())
      return `陽曆 ${d.getUTCFullYear()}年 ${d.getUTCMonth() + 1}月 ${d.getUTCDate()}日`;
    else
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    return String(tsOrIso).slice(0, 10);
  }
}

/* --- component --- */
export default function DetailAgenda({ selectedAgenda, onClose, editUrl }) {

  // ===== 1. Normalisasi src lebih dulu (AMAN karena bukan hook) =====
  const src = selectedAgenda?.attributes
    ? selectedAgenda.attributes
    : selectedAgenda || {};

  const agendaDate = src?.agendaDate || null;

  // ===== 2. Hooks =====
  const [lunarInfo, setLunarInfo] = useState(null);

  useEffect(() => {
    let mounted = true;

    if (agendaDate) {
      fetchLunarForIntDate(agendaDate)
        .then((res) => {
          if (mounted) setLunarInfo(res);
        })
        .catch(() => {
          if (mounted) setLunarInfo(null);
        });
    } else {
      setLunarInfo(null);
    }

    return () => {
      mounted = false;
    };
  }, [agendaDate]);

  // ===== 3. Early return BOLEH setelah hook =====
  if (!selectedAgenda) {
    return (
      <div className="detail-agenda" aria-live="polite">
        <style dangerouslySetInnerHTML={{ __html: css }} />
        <div className="empty">Pilih agenda untuk melihat detailnya.</div>
      </div>
    );
  }

  const subject_zh = decodeText(src.subject_zh || "");
  const subject = decodeText(src.subject || "");
  const agendaDateEnd = src.agendaDateEnd || null;
  const hoursInfo = decodeText(src.hoursInfo || "");
  // area relation: expect object with name or shortname; fallback to string
  const area = src.area ? (src.area.name || src.area.shortname || (typeof src.area === "string" ? src.area : "")) : "";
  const isFaHui = Boolean(src.FaHui);
  const isHighlight = Boolean(src.Highlight);
  const allowPre = Boolean(src.allowPreRegistration);
  const onlineClass = Boolean(src.onlineClass);
  const notes = decodeText(src.notes || "");
  const ageGroups = Array.isArray(src.category_age_groups) ? src.category_age_groups.map(g => g.name || g).filter(Boolean) : [];

  // possible presenter fields
  const presenter = src.agenda_master ? (src.agenda_master.displayName || src.agenda_master.name || "") : (src.recordOwner || "");
  // possible Chinese name (common patterns)
  const chineseName = src.agenda_master?.chineseName || src.chineseName || src.recordOwnerChineseName || "";

  const areaLabel =
        src?.area?.name ||
        (src?.fotang_external ? (src?.fotang_external.fotangNameCH + " " + src?.fotang_external.fotangNamePinyin) : "") ||
        src?.externalDanWeiOtherDesc ||
        null;

  let areaCat = null;
  if (areaLabel && (areaLabel === (src?.fotang_external?.fotangNameCH + " " + src?.fotang_external?.fotangNamePinyin) || areaLabel === src?.externalDanWeiOtherDesc)) {
    // from fotang_external or externalDanWeiOtherDesc
    areaCat = 3;
  } else if (src?.area) {
    const outer = src.area?.region?.outerRegion;
    areaCat = (outer === true) ? 2 : 1;
  }

  return (
    <div className="detail-agenda" role="region" aria-labelledby="detail-agenda-title">
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div className="da-toprow">
        <div className="da-dateblock" aria-hidden="true">
          {/* left date block: weekday / main date */}
          <div className="da-week">{agendaDate ? formatWeekday(agendaDate) : "-"}</div>
          <div className="da-date-main">{agendaDate ? formatDateShort(agendaDate) : "—"}</div>
          {/* ===== Lunar kecil di bawah ===== */}
          {lunarInfo && (
            <div
              className="da-date-range"
              style={{ marginTop: "0.125rem", fontSize: "0.75rem" }}
            >
              歲次{lunarInfo.lunarGanZhi}{" "}
              {lunarInfo.leapMonth ? "閏" : ""}
              {lunarInfo.lunarMonth}月{lunarInfo.lunarDay}日
            </div>
          )}
        </div>

        {/* right small range / end date */}
        <div style={{ textAlign: "right" }}>
          <div className="da-date-range">{agendaDateEnd ? `s/d ${formatDateShort(agendaDateEnd)}` : ""}</div>
          {hoursInfo ? <div className="da-date-range" style={{ marginTop: "0.25rem" }}>{hoursInfo}</div> : null}
        </div>
      </div>

      <div className="da-titlewrap">
        {areaLabel ? (
          <div
            className={`area-badge ${areaCat ? "area-cat" + areaCat : ""}`}
            aria-hidden="true"
            title={decodeText(areaLabel)}
          >
            {decodeText(areaLabel)}
          </div>
        ) : null}

        {/* Title: if there's an editUrl, make it a Link; otherwise plain text styled as link */}
        {editUrl ? (
          <Link id="detail-agenda-title" to={editUrl} className="da-title">{subject_zh || "(Tanpa judul)"}</Link>
        ) : (
          <div id="detail-agenda-title" className="da-title" role="heading">{subject_zh || "(Tanpa judul)"}</div>
        )}

        {/* small subtitle lines: presenter (latin) and chinese name if any */}
        <div className="da-subtitle">{ subject || capitalizePinyin(toPinyinSync(subject_zh)) }</div>
      </div>

      {(allowPre || ageGroups.length || isFaHui || isHighlight || onlineClass) && (
        <div className="da-row" aria-label="Informasi tambahan">
          <div className="da-meta">
            {allowPre ? <div className="meta-pill">Pre-registration</div> : null}
            {ageGroups.length ? <div className="meta-pill">Usia: {ageGroups.join(", ")}</div> : null}
            {isFaHui ? <div className="meta-pill">FaHui</div> : null}
            {isHighlight ? <div className="meta-pill">Highlight</div> : null}
            {onlineClass ? <div className="meta-pill">Online</div> : null}
          </div>
        </div>
      )}

      {notes ? (
        <div className="da-row" aria-label="Catatan">
          <div className="da-label">Catatan</div>
          <div className="da-notes">{notes}</div>
        </div>
      ) : null}

      {/* Sub-card: petugas utama */}
      <DetailPersonnel selectedAgenda={selectedAgenda} />
      <CardAgendaActions agenda={selectedAgenda} />
      
      <div className="actions" role="toolbar" aria-label="Detail actions">
        <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close detail panel">{isZh() ? "關閉" : "Close"}</button>
      </div>
    </div>
  );
}

DetailAgenda.propTypes = {
  selectedAgenda: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
  onClose: PropTypes.func,
  editUrl: PropTypes.string,
};

DetailAgenda.defaultProps = {
  selectedAgenda: null,
  onClose: () => {},
  editUrl: "",
};
